/**
 * **步进器** —— `[<] 12 [>]`，每点一下改一档。
 *
 * 两个键是**真的印章按钮**（`stampButton`），所以它们的状态反馈
 * 自动和别处一致 —— 这就是"组件化"最实际的收益：改一次状态语汇，
 * 全界面一起变。
 *
 * 读数住在一个**印出来的格子里**（不是裸数字）：印刷品里的表格读数
 * 都有自己的框，裸数字读起来像"浮在版面上的标签"。
 */

import { Container, Graphics } from 'pixi.js';
import { GAP, INK, uiLine, uiPad } from './style';
import { stampButton } from './button';
import { richLabel } from './richText';
import { drawRedactionSlash, stampInk } from './state';
import type { Widget } from './base';

export interface StepperSpec {
  readonly label: string;
  readonly scale: number;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step?: number;
  readonly onChange: (value: number) => void;
  readonly width?: number;
  readonly enabled?: boolean;
  /** 标签用正文色（而不是暗一档）。重要的一行用它 */
  readonly bold?: boolean;
}

export interface Stepper extends Widget {
  setValue(value: number): void;
  setEnabled(enabled: boolean): void;
}

export function stepper(spec: StepperSpec): Stepper {
  const scale = spec.scale;
  const step = spec.step ?? 1;
  const buttonSize = uiPad(24, scale);
  const width = spec.width ?? 320;
  const valueWidth = uiPad(52, scale);
  const height = buttonSize;
  const edge = uiLine(1, scale);

  const container = new Container();
  const name = richLabel({
    text: spec.label,
    scale,
    size: 12,
    fill: spec.bold === true ? INK.text : INK.textDim,
    role: 'cjk',
    bold: spec.bold,
  });

  const cell = new Graphics();
  const readout = richLabel({ text: '', scale, size: 12, fill: INK.text, role: 'mono', align: 'center' });

  const minus = stampButton({
    label: '<',
    scale,
    variant: 'secondary',
    size: 12,
    width: buttonSize,
    height: buttonSize,
    onPress: () => bump(-step),
  });
  const plus = stampButton({
    label: '>',
    scale,
    variant: 'secondary',
    size: 12,
    width: buttonSize,
    height: buttonSize,
    onPress: () => bump(step),
  });

  container.addChild(name, cell, readout, minus.container, plus.container);
  name.position.set(0, Math.round((height - name.height) / 2));

  const controlsX = width - buttonSize * 2 - valueWidth;
  const startX = Math.max(name.width + uiPad(GAP.normal, scale), controlsX);
  minus.container.position.set(startX, 0);
  plus.container.position.set(startX + buttonSize + valueWidth, 0);

  let value = spec.value;
  let enabled = spec.enabled ?? true;

  const paint = (): void => {
    readout.text = String(value);
    readout.position.set(startX + buttonSize + valueWidth / 2, Math.round((height - readout.height) / 2));

    // 读数的格子：印一个框，禁用时撤墨 + 划杠
    cell.clear();
    const cellX = startX + buttonSize;
    if (!enabled) {
      cell.rect(cellX, 0, valueWidth, height).fill({ color: INK.panel });
      cell.rect(cellX, 0, valueWidth, height).stroke({ color: stampInk('secondary', 'disabled').edge ?? INK.rule, width: edge, alignment: 0 });
      drawRedactionSlash(cell, { x: cellX, width: valueWidth, height }, scale, INK.rule);
    } else {
      cell.rect(cellX, 0, valueWidth, height).fill({ color: INK.panel });
      cell.rect(cellX, 0, valueWidth, height).stroke({ color: INK.rule, width: edge, alignment: 0 });
    }
    readout.style.fill = enabled ? INK.text : INK.textDim;
    name.style.fill = enabled
      ? spec.bold === true
        ? INK.text
        : INK.textDim
      : INK.textDim;

    minus.setEnabled(enabled && value > spec.min);
    plus.setEnabled(enabled && value < spec.max);
  };

  function bump(delta: number): void {
    const next = clamp(value + delta, spec.min, spec.max);
    if (next === value) return;
    value = next;
    paint();
    spec.onChange(value);
  }

  paint();

  return {
    container,
    width,
    height,
    setValue(next: number) {
      value = clamp(next, spec.min, spec.max);
      paint();
    },
    setEnabled(next: boolean) {
      enabled = next;
      paint();
    },
  };
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return value < min ? min : value > max ? max : value;
}
